// Pallet state machine — TRS §5.3 (extended 2026-05-28 per customer feedback).
//
// Allowed transitions (do not invent more):
//
//   Expected ──► Arrived ──► (PartiallyDispatched ⇄) ──► Dispatched
//       │            │                 │            │
//       │            ├──► Damaged ─────┼──► (Dispatched, Repacked, Lost)
//       │            ├──► Repacked ◄───┘
//       │            └──► Lost
//       └──► Cancelled (never arrived)
//
// Pure module: no I/O, no Prisma. The API service layer applies these
// rules and writes the PalletStateChange + AuditLog rows.
export const PALLET_STATES = [
    'EXPECTED',
    'ARRIVED',
    'PARTIALLY_DISPATCHED',
    'DISPATCHED',
    'UNPACKED_TO_STOCK',
    'DAMAGED',
    'REPACKED',
    'LOST',
    'CANCELLED',
];
/// States from which no transition is permitted.
export const TERMINAL_STATES = new Set([
    'DISPATCHED',
    'UNPACKED_TO_STOCK',
    'REPACKED',
    'LOST',
    'CANCELLED',
]);
/// Side states — billing implications. TRS §5.3: storage billing stops on the
/// day a side state is entered (unless admin overrides).
export const SIDE_STATES = new Set(['DAMAGED', 'LOST']);
/// `from → allowedNextStates`. Self-loop on PARTIALLY_DISPATCHED encodes the
/// "⇄" in the TRS diagram (multiple partial dispatches recorded as
/// successive PalletStateChange rows without changing state).
const TRANSITIONS = {
    EXPECTED: ['ARRIVED', 'CANCELLED'],
    ARRIVED: ['PARTIALLY_DISPATCHED', 'DISPATCHED', 'DAMAGED', 'REPACKED', 'LOST'],
    PARTIALLY_DISPATCHED: ['PARTIALLY_DISPATCHED', 'DISPATCHED', 'DAMAGED', 'REPACKED', 'LOST'],
    DISPATCHED: [],
    UNPACKED_TO_STOCK: [],
    DAMAGED: ['DISPATCHED', 'REPACKED', 'LOST'],
    REPACKED: [],
    LOST: [],
    CANCELLED: [],
};
export function canTransition(from, to) {
    return TRANSITIONS[from].includes(to);
}
export function nextStates(from) {
    return TRANSITIONS[from];
}
export function isTerminal(state) {
    return TERMINAL_STATES.has(state);
}
export function isSideState(state) {
    return SIDE_STATES.has(state);
}
export class PalletTransitionError extends Error {
    from;
    to;
    constructor(from, to) {
        super(`Pallet transition ${from} → ${to} is not allowed`);
        this.from = from;
        this.to = to;
        this.name = 'PalletTransitionError';
    }
}
/**
 * Asserts the transition is legal. Throws `PalletTransitionError` if not.
 * Use at every state-change call site so the rule is enforced regardless of
 * which controller path got there.
 */
export function assertTransition(from, to) {
    if (!canTransition(from, to))
        throw new PalletTransitionError(from, to);
}
/**
 * Side-states (DAMAGED, LOST) freeze billing on the day they are entered.
 * Returns the date through which storage charges accrue, given an arrival
 * date and the resolution event (dispatch / damage / loss / today).
 */
export function billingThroughDate(args) {
    let lifecycleEnd;
    if ((args.state === 'DISPATCHED' ||
        args.state === 'UNPACKED_TO_STOCK' ||
        args.state === 'REPACKED') &&
        args.dispatchedAt) {
        lifecycleEnd = args.dispatchedAt;
    }
    else if (SIDE_STATES.has(args.state) && args.sideStateAt) {
        lifecycleEnd = args.sideStateAt;
    }
    else if (args.state === 'CANCELLED') {
        lifecycleEnd = args.arrivedAt; // never accrued
    }
    else {
        lifecycleEnd = args.today;
    }
    return args.depletedAt && args.depletedAt < lifecycleEnd ? args.depletedAt : lifecycleEnd;
}
//# sourceMappingURL=pallet-state.js.map